import { useTranslation } from 'react-i18next';
import { InvestorService } from '../../../investorService';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { InvestorStatus } from '../../../../../constants/investorStatus';
import { useEffect } from 'react';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { getUser, Logout } from '../../../../../services/authService';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { Box } from '@mui/material';
import { InvestorDocuments } from '../documents';
import { InvestorPersonalInfo } from '../personalInfo';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { Section } from '../../../../../shared/components/common/section';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { ConfirmPasswordDialog } from '../../../../../shared/components/confirmPasswordDialog';
import { CorporateInformations } from '../corporateInfo';
import { InvestorType } from '../../../../../constants/investorType';
import { InvestorBankDetails } from '../../../../../shared/components/investorBankDetails';
import { IS_SA } from '../../../../../config/appConfig';
import { useInvestorDetails } from '../../../../../context/investorDetailContext';
import { SubscriptionService } from '../../../../../services/subscriptionService';

export const InvestorUpdateDeatils = () => {
	const investorService = new InvestorService();
	const subscriptionService = new SubscriptionService();
	const { investorDetails, setInvestorDetails } = useInvestorDetails();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [showOTPAuth, setShowOTPAuth] = useState(false);
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const [isArchived, setIsArchived] = useState(false);
	const [isSubscribed, setIsSubscribed] = useState(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);

	const isApprovedByCEO = () => {
		return getUser()?.status === InvestorStatus.ApprovedByCO;
	};

	useEffect(() => {
		if (investorDetails && investorDetails?.status?.name) {
			setIsArchived(investorDetails?.status?.name === InvestorStatus.Archived);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [investorDetails]);

	useEffect(() => {
		const userEmail = getUser()?.email;
		if (userEmail) {
			checkSubscriptionStatus(userEmail);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkSubscriptionStatus = (email) => {
		if (!email) return;
		setSubscriptionLoading(true);
		subscriptionService
			.checkSubscriptionStatus(email)
			.then((resp) => {
				const subscribed = resp.data?.subscribed ?? false;
				setIsSubscribed(subscribed);
				setSubscriptionLoading(false);
			})
			.catch((err) => {
				setIsSubscribed(false);
				setSubscriptionLoading(false);
			});
	};

	const handleSubscriptionChange = (subscriptionValue) => {
		if (subscriptionValue === null || subscriptionValue === undefined) return;
		
		const userEmail = getUser()?.email;
		if (!userEmail) {
			Toaster({ msg: 'Email not available', isError: true });
			return;
		}

		setSubscriptionLoading(true);
		const serviceCall = subscriptionValue
			? subscriptionService.subscribe(userEmail)
			: subscriptionService.unsubscribe(userEmail);

		serviceCall
			.then((resp) => {
				setIsSubscribed(Boolean(subscriptionValue));
				setSubscriptionLoading(false);
				Toaster({
					msg: subscriptionValue 
						? (t('subscribedSuccessfully') || 'Subscribed successfully')
						: (t('unsubscribedSuccessfully') || 'Unsubscribed successfully'),
					isError: !subscriptionValue
				});
			})
			.catch((err) => {
				setSubscriptionLoading(false);
				Toaster({ msg: t('errorOccurred') || 'An error occurred', isError: true });
			});
	};

	const getInvestorDetails = () => {
		investorService
			.getInvestor()
			.then((resp) => {
				setInvestorDetails(resp.data);
			})
			.catch((err) => { });
	};

	const onSavePersonalInfo = () => {
		getInvestorDetails();
	};

	const onCloseOTPAuth = (value) => {
		setShowOTPAuth(false);
		if (value) {
			checkActivateOrDeactivate();
		}
	};

	const checkActivateOrDeactivate = () => {
		if (isArchived) {
			onActivate();
		} else {
			onDeactivate();
		}
	};

	const onActivate = () => {
		investorService
			.activateAccount()
			.then((resp) => {
				Toaster({ msg: t('accountDeactivated') });
				onAfterActiveDeactive();
			})
			.catch((err) => { });
	};
	const onDeactivate = () => {
		investorService
			.deactivateAccount()
			.then((resp) => {
				Toaster({ msg: t('accountActivated') });
				onAfterActiveDeactive();
			})
			.catch((err) => { });
	};

	const onAfterActiveDeactive = () => {
		Logout();
		navigate(`${buildLocalizeRoute(AppRoutes.Login)}`);
	};

	const onPasswordDialogClose = (value) => {
		setShowPasswordDialog(false);
		if (value) {
			checkActivateOrDeactivate();
		}
	};

	const isKSAProfessional = () => {
		if (!IS_SA) {
			return true;
		}

		if (investorDetails) {
			return IS_SA && investorDetails?.investorType?.name === InvestorType.Professional;
		}
		return false;
	};

	return (
		<Box className="flex flex-col">
			<Box className="grid-flow-row grid gap-3">
				{investorDetails && !isApprovedByCEO() && isKSAProfessional() ? (
					<InvestorDocuments
						investorType={
							investorDetails?.investorType?.name
						}></InvestorDocuments>
				) : null}
				<InvestorBankDetails />
				<InvestorPersonalInfo
					investorDetails={investorDetails}
					onSavePersonalInfo={() => {
						onSavePersonalInfo();
					}}
					isSubscribed={isSubscribed}
					subscriptionLoading={subscriptionLoading}
					onSubscriptionChange={handleSubscriptionChange}
				/>
				{investorDetails?.investorType?.name === InvestorType.Corporate ? (
					<CorporateInformations
						id={investorDetails?.id}></CorporateInformations>
				) : null}
				<Section
					children={
						<TextWrapper
							classList="!info-text"
							text={
								<div>
									<div>
										{t('changeSignInDetailsAdminText')}{' '}
										<a
											className="underline-link"
											href="mailto:support@fundingsouq.com">
											support@fundingsouq.com
										</a>
									</div>
								</div>
							}></TextWrapper>
					}></Section>
			</Box>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Other}
					state={showOTPAuth}
					onClose={(e) => {
						onCloseOTPAuth(e);
					}}></OTPAuthenticationDialog>
			) : null}
			{showPasswordDialog ? (
				<ConfirmPasswordDialog
					state={showPasswordDialog}
					onClose={(e) => {
						onPasswordDialogClose(e);
					}}></ConfirmPasswordDialog>
			) : null}
		</Box>
	);
};
